export const sanitizeDatetimeLocal = (value: string): string => {
  if (!value) return value;
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  const hasT = trimmed.includes("T");
  const rawDatePart = hasT ? trimmed.split("T")[0] : trimmed.split(",")[0];
  const rawTimePart = hasT ? trimmed.split("T")[1] : trimmed.split(",")[1]?.trim() ?? "";

  let separator = "-";
  if (rawDatePart.includes("/")) {
    separator = "/";
  } else if (rawDatePart.includes(".")) {
    separator = ".";
  }

  if (!rawDatePart.includes(separator)) {
    return trimmed;
  }

  const parts = rawDatePart.split(separator);
  if (parts.length < 3) {
    return trimmed;
  }

  let yearIndex = 0;
  if (parts[0].length !== 4 && parts[2].length >= 4) {
    yearIndex = 2;
  }

  if (parts[yearIndex].length > 4) {
    parts[yearIndex] = parts[yearIndex].slice(0, 4);
  }

  const sanitizedDate = parts.join(separator);

  if (hasT) {
    return rawTimePart ? `${sanitizedDate}T${rawTimePart}` : sanitizedDate;
  }

  if (rawTimePart) {
    return `${sanitizedDate}, ${rawTimePart}`;
  }

  return sanitizedDate;
};

const DATE_PART_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_PART_PATTERN = /T(\d{2}:\d{2})/;

const pad = (value: number) => String(value).padStart(2, "0");

const buildUtcDate = (datePart: string): Date | null => {
  const match = datePart.match(DATE_PART_PATTERN);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day));
};

export const extractDatePart = (value?: string | null): string | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(DATE_PART_PATTERN);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().split("T")[0];
};

export const extractTimePart = (value?: string | null): string | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(TIME_PART_PATTERN);
  if (match) {
    return match[1];
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

export const formatCalendarDate = (
  value: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  const datePart = extractDatePart(value);
  if (!datePart) return "";

  const utcDate = buildUtcDate(datePart);
  if (!utcDate) return "";

  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: "UTC",
  }).format(utcDate);
};
